// src/pages/ForceRefresh.jsx
import React, { useState } from 'react';
import { FiRefreshCw, FiUser, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ForceRefresh = () => {
  const [refreshStatus, setRefreshStatus] = useState('idle');
  const { currentUser, loadUserProfile, userProfile, userRole } = useAuth();
  const navigate = useNavigate();

  const handleForceRefresh = async () => {
    if (!currentUser) {
      toast.error('Please log in first');
      return;
    }

    try {
      setRefreshStatus('running');
      
      console.log('🔄 Force refreshing user profile...');
      console.log('Current user:', currentUser.uid, currentUser.email);
      
      // Force reload the user profile
      const profile = await loadUserProfile(currentUser.uid);
      console.log('📋 Loaded profile:', profile);
      
      setRefreshStatus('success');
      toast.success('Profile refreshed successfully!');
      
      // Redirect to admin if user has admin role
      setTimeout(() => {
        if (profile?.role?.toLowerCase() === 'admin' || profile?.role === 'Admin') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }, 2000);
      
    } catch (err) {
      console.error('Refresh failed:', err);
      setRefreshStatus('error');
      toast.error('Refresh failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <FiRefreshCw className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Refresh Profile
            </h1>
            <p className="text-gray-600">
              Force reload your user profile and permissions
            </p>
          </div>

          {currentUser && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-blue-900 mb-2">Current User:</h3>
              <p className="text-blue-700 mb-1">Email: {currentUser.email}</p>
              <p className="text-blue-700 mb-1">UID: {currentUser.uid}</p>
              {userProfile && (
                <>
                  <p className="text-blue-700 mb-1">Role: {userProfile.role || 'Not set'}</p>
                  <p className="text-blue-700">Name: {userProfile.firstName} {userProfile.lastName}</p>
                </>
              )}
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">This will:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Force reload your user profile from Firestore</li>
                <li>• Refresh your role and permissions</li>
                <li>• Update the authentication context</li>
                <li>• Redirect you to the appropriate page</li>
              </ul>
            </div>

            <div className="flex justify-center">
              {refreshStatus === 'idle' && (
                <button
                  onClick={handleForceRefresh}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
                >
                  <FiRefreshCw className="h-4 w-4" />
                  Force Refresh Profile
                </button>
              )}
              
              {refreshStatus === 'running' && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Refreshing profile...</span>
                </div>
              )}
              
              {refreshStatus === 'success' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <FiCheck className="h-5 w-5" />
                    <span className="font-medium">Profile Refreshed!</span>
                  </div>
                  <p className="text-sm text-gray-600">Redirecting...</p>
                </div>
              )}
              
              {refreshStatus === 'error' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
                    <FiAlertCircle className="h-5 w-5" />
                    <span className="font-medium">Refresh Failed</span>
                  </div>
                  <button
                    onClick={handleForceRefresh}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForceRefresh;
